import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { collection, getDocs, doc, updateDoc, query, where } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LuBuilding2, LuLoaderCircle, LuSave, LuWalletCards } from 'react-icons/lu';
import { db } from '../../config/firebaseConfig';
import { SelectInput } from '../../components/form/FormControls';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import { isArchivedAssignment, getArchivedAssignmentLabel } from '../../utils/assignmentHistory';
import useEmployeeAnalysisText from './employeeAnalysisText';

/**
 * FTE Yönetimi: the employee's assignments in every project (archived
 * centers included, marked) with an FTE tariff of that project for each;
 * saving writes the chosen tariff into Projects.employees[].rate (as before).
 */
const FTEModal = ({ show, onClose, employee, onSaved }) => {
  const { language, label } = useEmployeeAnalysisText();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [projectFTEs, setProjectFTEs] = useState({});

  const fetchEmployeeProjects = useCallback(async () => {
    if (!employee?.id) return;
    setLoading(true);
    try {
      const projectsSnapshot = await getDocs(collection(db, 'Projects'));
      const employeeProjects = [];
      const currentFTEs = {};
      projectsSnapshot.docs.forEach((projectDoc) => {
        const projectData = projectDoc.data();
        (projectData.employees || []).filter((emp) => emp.id === employee.id).forEach((emp) => {
          employeeProjects.push({
            projectId: projectDoc.id,
            projectTitle: projectData.titleShort || projectData.title || 'N/A',
            centerName: emp.centerName || 'N/A',
            centerId: emp.centerId,
            role: emp.role,
            currentRate: emp.rate,
            // Left centers stay listed (their past FTE stays visible), marked
            isArchived: isArchivedAssignment(emp),
            unassignedAt: emp.unassignedAt || '',
          });
          const key = `${projectDoc.id}-${emp.centerId}`;
          if (emp.rate && emp.rate.type?.toUpperCase() === 'FTE') currentFTEs[key] = emp.rate.id || '';
        });
      });
      setProjects(employeeProjects);
      setProjectFTEs(currentFTEs);
    } catch (error) {
      console.error('Error fetching employee projects:', error);
      toast.error(label('fte.projectsError'));
    } finally {
      setLoading(false);
    }
  }, [employee, label]);

  const fetchTariffs = useCallback(async () => {
    try {
      const tariffsSnapshot = await getDocs(collection(db, 'Tariffs'));
      const fteTariffsData = tariffsSnapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((t) => t.type?.toUpperCase() === 'FTE')
        .sort((a, b) => parseFloat(a.value) - parseFloat(b.value));
      setTariffs(fteTariffsData);
    } catch (error) {
      console.error('Error fetching tariffs:', error);
      toast.error(label('fte.tariffsError'));
    }
  }, [label]);

  useEffect(() => {
    if (show && employee) {
      fetchEmployeeProjects();
      fetchTariffs();
    }
  }, [show, employee, fetchEmployeeProjects, fetchTariffs]);

  const handleFTEChange = (projectId, centerId, tariffId) => {
    setProjectFTEs((current) => ({ ...current, [`${projectId}-${centerId}`]: tariffId }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Group the choices by project
      const projectUpdates = {};
      projects.forEach((project) => {
        const selectedTariffId = projectFTEs[`${project.projectId}-${project.centerId}`];
        if (!projectUpdates[project.projectId]) projectUpdates[project.projectId] = [];
        projectUpdates[project.projectId].push({ centerId: project.centerId, tariffId: selectedTariffId });
      });

      for (const [projectId, updates] of Object.entries(projectUpdates)) {
        const projectRef = doc(db, 'Projects', projectId);
        const projectDoc = await getDocs(query(collection(db, 'Projects'), where('__name__', '==', projectId)));
        if (projectDoc.empty) continue;
        const projectData = projectDoc.docs[0].data();
        const employees = projectData.employees || [];
        const updatedEmployees = employees.map((emp) => {
          if (emp.id !== employee.id) return emp;
          const update = updates.find((u) => u.centerId === emp.centerId);
          if (!update) return emp;
          const selectedTariff = tariffs.find((t) => t.id === update.tariffId);
          if (selectedTariff) {
            return { ...emp, rate: { id: selectedTariff.id, type: selectedTariff.type, value: selectedTariff.value, price: selectedTariff.price } };
          }
          return emp;
        });
        await updateDoc(projectRef, { employees: updatedEmployees });
      }

      if (onSaved) onSaved();
    } catch (error) {
      console.error('Error saving FTE:', error);
      toast.error(label('fte.saveError'));
    } finally {
      setSaving(false);
    }
  };

  let body;
  if (loading) {
    body = (
      <p className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-10 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
        <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
        {label('common.loading')}
      </p>
    );
  } else if (projects.length === 0) {
    body = (
      <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-py-10 tw-text-center">
        <LuBuilding2 aria-hidden="true" className="tw-h-8 tw-w-8 tw-text-slate-300 dark:tw-text-slate-600" />
        <p className="tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('fte.empty')}</p>
      </div>
    );
  } else {
    body = (
      <ul className="tw-space-y-2.5">
        {projects.map((project, index) => {
          const key = `${project.projectId}-${project.centerId}`;
          const projectTariffs = tariffs.filter((tariff) => tariff.project === project.projectId);
          return (
            <li key={`${key}-${index}`} className={`tw-grid tw-gap-3 tw-rounded-2xl tw-border tw-p-4 sm:tw-grid-cols-[minmax(0,1fr)_16rem] sm:tw-items-center ${project.isArchived ? 'tw-border-dashed tw-border-slate-300 tw-bg-slate-50/60 dark:tw-border-white/15 dark:tw-bg-white/[0.02]' : 'tw-border-slate-200/80 tw-bg-white/70 dark:tw-border-white/10 dark:tw-bg-white/[0.03]'}`}>
              <div className="tw-min-w-0">
                <p className="tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{project.projectTitle}</p>
                <div className="tw-mt-1.5 tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
                  <Badge tone="info"><LuBuilding2 aria-hidden="true" className="tw-h-3 tw-w-3" />{project.centerName}</Badge>
                  {project.role && <Badge tone="accent">{project.role}</Badge>}
                  {project.isArchived && <Badge tone="warning">{getArchivedAssignmentLabel(project)}</Badge>}
                </div>
                {project.currentRate && (
                  <p className="tw-mt-1.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('fte.current', { type: project.currentRate.type, value: project.currentRate.value })}</p>
                )}
              </div>
              <label className="tw-block">
                <span className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label('fte.choose')}</span>
                <SelectInput
                  value={projectFTEs[key] || ''}
                  onChange={(event) => handleFTEChange(project.projectId, project.centerId, event.target.value)}
                  placeholder={label('fte.placeholder')}
                  options={projectTariffs.map((tariff) => ({ value: tariff.id, label: `${tariff.type} - ${tariff.value}` }))}
                />
                {projectTariffs.length === 0 && <span className="tw-mt-1 tw-block tw-text-[11px] tw-text-slate-400">{label('fte.noTariffs')}</span>}
              </label>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <Modal
      isOpen={Boolean(show)}
      onClose={onClose}
      size="lg"
      dismissible={!saving}
      title={label('fte.title')}
      subtitle={label('fte.subtitle', { name: employee?.name || '' })}
      closeLabel={label('common.cancel')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30" size="xs">
          <LuWalletCards aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>{label('common.cancel')}</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving || loading || projects.length === 0}>
            {saving ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {saving ? label('common.saving') : label('common.save')}
          </Button>
        </>
      )}
    >
      {body}
    </Modal>
  );
};

FTEModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  employee: PropTypes.object,
  onSaved: PropTypes.func,
};

export default FTEModal;
